import React, { useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Card, Row } from '@cuemath/comps';
import { Text, Button } from '@cuemath/web-comps';
import { usePostMessage } from 'actions/support-agent';
import COLORS from 'constants/colors';
import { commitDraftToList } from '../utils';
import { ERROR_AUTO_CLEAR_MS, FROM_EMAIL_OPTIONS } from '../constants';
import {
  ModalOverlay,
  ModalBody,
  ModalErrorMsg,
  EmailFieldsWrap,
  EmailFieldRow,
  EmailFieldLabel,
  EmailFieldInput,
  BccToggle,
  IconBtn,
  FilterDropdownWrap,
  FilterDropdownTrigger,
  FilterDropdownMenu,
  FilterDropdownItem,
  CcBccToggleRow,
  ComposeInternalBanner,
  HiddenFileInput,
  AttachmentChip,
  AttachmentChipName,
  AttachmentChipRemove,
  AttachBtn,
  EmailComposerArea,
  SA_COLORS,
} from '../styled-comps';
import Icon from './icon';
import AttachIcon from 'img/paperclip.svg';
import useAttachmentUpload from './use-attachment-upload';
import RecipientChipsInput from './recipient-chips-input';

// ── Styled Components ─────────────────────────────────────────────────

const ModalCardWide = styled(Card)`
  width: 90%;
  max-width: 620px;
  max-height: 80vh;
  border: 1px solid ${SA_COLORS.BORDER_TERTIARY};
`;

const HeadTitleRow = styled(Row)`
  gap: 10px;
`;

const ModalHead = styled(Row)`
  background: ${SA_COLORS.BG_PRIMARY};
  border-bottom: 1px solid ${SA_COLORS.BORDER_TERTIARY};
`;

const HeadCloseBtn = styled(IconBtn)`
  width: 32px;
  height: 32px;
  color: ${SA_COLORS.TEXT_SECONDARY};

  &:hover {
    background: ${SA_COLORS.BG_TERTIARY};
  }
`;

const EmailModalFoot = styled(Row)`
  border-top: 1px solid ${SA_COLORS.BORDER_TERTIARY};
`;

const FootActions = styled(Row)`
  gap: 8px;
`;

const FromTrigger = styled(FilterDropdownTrigger)`
  border: none;
  background: transparent;
  padding: 2px 0;
`;

const ChevIcon = styled(Icon)`
  transition: transform 0.15s ease;
  ${({ $open }) => $open && 'transform: rotate(180deg);'}
`;

const ModalEmailFieldRow = styled(EmailFieldRow)`
  padding: 4px 0;
  border-bottom: 1px solid ${SA_COLORS.BORDER_TERTIARY};

  & input {
    border-bottom: none;
  }
`;

const AttachRow = styled(Row)`
  gap: 10px;
  flex-wrap: wrap;
`;

const CancelBtn = styled(Button)`
  border-radius: 0;
  padding: 6px 16px;
  border-color: ${SA_COLORS.BORDER_PRIMARY};
  background: ${SA_COLORS.BG_PRIMARY};

  &:hover:not([disabled]) {
    background: ${SA_COLORS.BG_SECONDARY};
  }

  &:hover:not([disabled]) * {
    color: ${SA_COLORS.TEXT_PRIMARY};
  }
`;

const SendBtn = styled(Button)`
  border-radius: 0;
  padding: 6px 16px;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not([disabled]) {
    opacity: 0.9;
  }
`;

// ── Component ─────────────────────────────────────────────────────────

const EmailModal = ({ open, onClose, ticketId, onTicketUpdated, onSent }) => {
  const [fromEmail, setFromEmail] = useState('');
  const [fromOpen, setFromOpen] = useState(false);
  const [toEmails, setToEmails] = useState([]);
  const [toDraft, setToDraft] = useState('');
  const [subject, setSubject] = useState('');
  const [ccEmails, setCcEmails] = useState([]);
  const [ccDraft, setCcDraft] = useState('');
  const [bccEmails, setBccEmails] = useState([]);
  const [bccDraft, setBccDraft] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [body, setBody] = useState('');
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const overlayMouseDownOnSelf = useRef(false);
  const pendingItemRef = useRef(null);

  const showError = useCallback(msg => {
    setError(msg);
    setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
  }, []);

  const {
    fileInputRef,
    uploading,
    setUploading,
    attachment,
    setAttachment,
    handleFileSelect,
  } = useAttachmentUpload({ onError: setError });

  const { post: postMessage } = usePostMessage({
    onComplete: err => {
      setSending(false);
      if (err != null) {
        setError(err || 'Failed to send email');
        setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
        pendingItemRef.current = null;
      } else {
        if (pendingItemRef.current) {
          onSent?.(pendingItemRef.current);
          pendingItemRef.current = null;
        }
        if (onTicketUpdated) onTicketUpdated();
        handleClose();
      }
    },
  });

  const handleClose = useCallback(() => {
    setFromEmail('');
    setFromOpen(false);
    setToEmails([]);
    setToDraft('');
    setSubject('');
    setCcEmails([]);
    setCcDraft('');
    setBccEmails([]);
    setBccDraft('');
    setShowCc(false);
    setShowBcc(false);
    setBody('');
    setAttachment(null);
    setUploading(false);
    setError(null);
    setSending(false);
    onClose();
  }, [onClose, setAttachment, setUploading]);

  const handleSend = useCallback(() => {
    if (!ticketId) {
      setError('No ticket selected');
      setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
      return;
    }
    if (!fromEmail) {
      setError('Please select a sender email');
      setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
      return;
    }

    // Fold each field's uncommitted draft into its chip list — the chip
    // input's setState hasn't applied yet in this closure, so a typed-but-
    // unentered recipient would otherwise be silently dropped.
    const to = commitDraftToList(toDraft, toEmails);
    const ccResult = commitDraftToList(ccDraft, ccEmails);
    const bccResult = commitDraftToList(bccDraft, bccEmails);
    const invalid = to.invalid || ccResult.invalid || bccResult.invalid;
    if (invalid) {
      setError(`Invalid recipient email: ${invalid}`);
      setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
      return;
    }
    const recipients = to.list;
    if (recipients.length === 0) {
      setError('At least one recipient is required');
      setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
      return;
    }
    if (!body.trim() && !attachment) {
      setError('Message body is required');
      setTimeout(() => setError(null), ERROR_AUTO_CLEAR_MS);
      return;
    }

    const ccList = ccResult.list;
    const bccList = bccResult.list;

    const tempId = crypto.randomUUID();
    const now = new Date();
    pendingItemRef.current = {
      id: tempId,
      rawTs: now.toISOString(),
      kind: 'external',
      who: 'agent',
      role: 'AGENT',
      direction: 'sent',
      externalKind: 'EMAIL',
      messageId: tempId,
      optimistic: true,
      body: body.trim(),
      ts: now.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
      htmlContent: null,
      emailSubject: subject.trim() || null,
      emailCc: ccList.length ? ccList : null,
      emailBcc: bccList.length ? bccList : null,
      emailToList: recipients,
      senderEmail: fromEmail.trim() || null,
      messageType: attachment ? attachment.messageType : 'TEXT',
      mediaUrl: attachment ? attachment.displayUrl || attachment.url : null,
    };

    setSending(true);
    postMessage(
      {
        type: 'EMAIL',
        content: body.trim(),
        message_type: attachment ? attachment.messageType : 'TEXT',
        ...(attachment ? { media_url: attachment.url } : {}),
        temp_id: tempId,
        to_email: recipients,
        from_email: fromEmail.trim() || undefined,
        email_subject: subject.trim() || undefined,
        email_cc: ccList.length ? ccList : undefined,
        email_bcc: bccList.length ? bccList : undefined,
      },
      ticketId,
    );
  }, [
    fromEmail,
    toEmails,
    toDraft,
    ccEmails,
    ccDraft,
    bccEmails,
    bccDraft,
    subject,
    body,
    attachment,
    ticketId,
    postMessage,
  ]);

  if (!open) return null;

  return (
    <ModalOverlay
      onMouseDown={e => {
        overlayMouseDownOnSelf.current = e.target === e.currentTarget;
      }}
      onClick={e => {
        if (overlayMouseDownOnSelf.current && e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <ModalCardWide onClick={e => e.stopPropagation()}>
        <ModalHead justifyContent="space-between" alignItems="center" gap={8} gutter={18}>
          <HeadTitleRow alignItems="center">
            <Icon name="mail" size={16} color={SA_COLORS.GOLD_700} />
            <Text size={15} weight={700} color={SA_COLORS.TEXT_PRIMARY}>
              Send an email
            </Text>
          </HeadTitleRow>
          <HeadCloseBtn onClick={handleClose}>
            <Icon name="close" size={16} color={SA_COLORS.TEXT_SECONDARY} />
          </HeadCloseBtn>
        </ModalHead>

        <ComposeInternalBanner
          flexDirection="row"
          gap={12}
          gutter={20}
          alignItems="center"
        >
          <Icon name="lock" size={14} color={SA_COLORS.GOLD_700} />
          <Text variant="body2">
            External side-conversation — visible to your team only, never to the customer.
            It appears inline in this ticket's transcript.
          </Text>
        </ComposeInternalBanner>

        <ModalBody>
          <EmailFieldsWrap>
            <ModalEmailFieldRow>
              <EmailFieldLabel>From</EmailFieldLabel>
              <FilterDropdownWrap>
                <FromTrigger type="button" onClick={() => setFromOpen(p => !p)}>
                  <Text
                    size={13}
                    color={fromEmail ? SA_COLORS.TEXT_PRIMARY : SA_COLORS.TEXT_TERTIARY}
                  >
                    {fromEmail || 'Select sender email'}
                  </Text>
                  <ChevIcon name="chev" size={14} $open={fromOpen} />
                </FromTrigger>
                {fromOpen && (
                  <FilterDropdownMenu>
                    {FROM_EMAIL_OPTIONS.map(email => (
                      <FilterDropdownItem
                        key={email}
                        onClick={() => {
                          setFromEmail(email);
                          setFromOpen(false);
                        }}
                      >
                        {email}
                      </FilterDropdownItem>
                    ))}
                  </FilterDropdownMenu>
                )}
              </FilterDropdownWrap>
            </ModalEmailFieldRow>
            <ModalEmailFieldRow>
              <EmailFieldLabel>To</EmailFieldLabel>
              <RecipientChipsInput
                value={toEmails}
                onChange={setToEmails}
                draft={toDraft}
                onDraftChange={setToDraft}
                placeholder="Type an email, then press Enter or comma"
                onError={showError}
              />
              <CcBccToggleRow>
                {!showCc && <BccToggle onClick={() => setShowCc(true)}>CC</BccToggle>}
                {!showBcc && <BccToggle onClick={() => setShowBcc(true)}>BCC</BccToggle>}
              </CcBccToggleRow>
            </ModalEmailFieldRow>
            {showCc && (
              <ModalEmailFieldRow>
                <EmailFieldLabel>CC</EmailFieldLabel>
                <RecipientChipsInput
                  value={ccEmails}
                  onChange={setCcEmails}
                  draft={ccDraft}
                  onDraftChange={setCcDraft}
                  placeholder="Type an email, then press Enter or comma"
                  onError={showError}
                />
              </ModalEmailFieldRow>
            )}
            {showBcc && (
              <ModalEmailFieldRow>
                <EmailFieldLabel>BCC</EmailFieldLabel>
                <RecipientChipsInput
                  value={bccEmails}
                  onChange={setBccEmails}
                  draft={bccDraft}
                  onDraftChange={setBccDraft}
                  placeholder="Type an email, then press Enter or comma"
                  onError={showError}
                />
              </ModalEmailFieldRow>
            )}
            <ModalEmailFieldRow>
              <EmailFieldLabel>Subject</EmailFieldLabel>
              <EmailFieldInput
                name="subject"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Email subject"
              />
            </ModalEmailFieldRow>
          </EmailFieldsWrap>

          <EmailComposerArea
            name="message"
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Write your message..."
            rows={6}
          />

          {error && <ModalErrorMsg>{error}</ModalErrorMsg>}
        </ModalBody>

        <EmailModalFoot
          justifyContent="space-between"
          alignItems="center"
          gap={12}
          gutter={20}
        >
          <AttachRow alignItems="center">
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.doc,.docx"
              onChange={handleFileSelect}
            />
            <AttachBtn
              type="button"
              variant="tertiary"
              width="auto"
              size="small"
              iconConfig={{ position: 'left', imgLink: AttachIcon }}
              color={SA_COLORS.TEXT_SECONDARY}
              onClick={() => fileInputRef.current?.click()}
              isDisabled={uploading}
            />
            {attachment && (
              <AttachmentChip gap={6} gutter={10} alignItems="center">
                <AttachmentChipName variant="body3" color={SA_COLORS.TEXT_PRIMARY}>
                  {attachment.name}
                </AttachmentChipName>
                <AttachmentChipRemove onClick={() => setAttachment(null)}>
                  <Icon name="close" size={12} />
                </AttachmentChipRemove>
              </AttachmentChip>
            )}
          </AttachRow>
          <FootActions alignItems="center">
            <CancelBtn
              type="button"
              variant="secondary"
              width="auto"
              size="small"
              color={SA_COLORS.TEXT_PRIMARY}
              label="Cancel"
              onClick={handleClose}
            />
            <SendBtn
              type="button"
              variant="tertiary"
              width="auto"
              size="small"
              color={COLORS.WHITE}
              backgroundColor={SA_COLORS.NEUTRAL_1000}
              label={sending ? 'Sending...' : 'Send email'}
              onClick={handleSend}
              isDisabled={sending || uploading}
            />
          </FootActions>
        </EmailModalFoot>
      </ModalCardWide>
    </ModalOverlay>
  );
};

export default EmailModal;
